import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  type CreateGroupPayload,
  type OBAStatus,
  type WhatsAppGroup,
  type WhatsAppGroupInfo,
  whatsappService,
} from "@/services/whatsapp.service/whatsapp.api";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  Link2,
  Loader2,
  Pencil,
  PlusCircle,
  RefreshCw,
  Trash2,
  UserMinus,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

// ─── Copy button ──────────────────────────────────────────────────────────────

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Não foi possível copiar");
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="shrink-0 rounded-md border border-border p-2 transition-colors hover:bg-accent"
      aria-label="Copiar"
    >
      {copied ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <Copy className="h-4 w-4 text-muted-foreground" />
      )}
    </button>
  );
}

// ─── Create form ──────────────────────────────────────────────────────────────

const createSchema = z.object({
  subject: z.string().min(1, "Nome obrigatório").max(128, "Máx. 128 caracteres"),
  description: z.string().max(2048, "Máx. 2048 caracteres").optional(),
  joinApprovalMode: z.enum(["auto_approve", "approval_required"] as const),
});

type CreateForm = z.infer<typeof createSchema>;

function CreateGroupForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: { subject: "", description: "", joinApprovalMode: "auto_approve" },
  });

  async function onSubmit(values: CreateForm) {
    setSubmitting(true);
    const payload: CreateGroupPayload = {
      subject: values.subject,
      description: values.description || undefined,
      join_approval_mode: values.joinApprovalMode,
    };
    const [res, err] = await whatsappService.createGroup(payload);
    setSubmitting(false);

    if (err || !res) {
      toast.error(err?.msg || "Erro ao criar grupo");
      return;
    }

    toast.success(`Grupo "${res.subject}" criado!`);
    form.reset();
    onSaved();
  }

  return (
    <div className="rounded-lg border border-border p-4">
      <h4 className="mb-4 font-medium">Criar grupo</h4>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome do grupo</FormLabel>
                <FormControl>
                  <Input placeholder="Ex: Clientes VIP" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descrição (opcional)</FormLabel>
                <FormControl>
                  <Textarea placeholder="Sobre o que é esse grupo" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="joinApprovalMode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Entrada de novos participantes</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="auto_approve">Entrada automática pelo link</SelectItem>
                    <SelectItem value="approval_required">Requer aprovação</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex gap-2">
            <Button type="submit" disabled={submitting} className="gap-2">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Criar grupo
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

// ─── Edit form (nome/descrição) ────────────────────────────────────────────────

const editSchema = z.object({
  subject: z.string().min(1, "Nome obrigatório").max(128, "Máx. 128 caracteres"),
  description: z.string().max(2048, "Máx. 2048 caracteres").optional(),
});

type EditForm = z.infer<typeof editSchema>;

function EditGroupForm({
  group,
  onClose,
  onSaved,
}: {
  group: WhatsAppGroup;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<EditForm>({
    resolver: zodResolver(editSchema),
    defaultValues: { subject: group.subject, description: group.description || "" },
  });

  async function onSubmit(values: EditForm) {
    setSubmitting(true);
    const [res, err] = await whatsappService.updateGroup(group.groupId, {
      subject: values.subject,
      description: values.description || undefined,
    });
    setSubmitting(false);

    if (err || !res) {
      toast.error(err?.msg || "Erro ao editar grupo");
      return;
    }

    toast.success("Grupo atualizado!");
    onSaved();
  }

  return (
    <div className="rounded-md border border-border bg-muted/30 p-3 space-y-3">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">Nome</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">Descrição</FormLabel>
                <FormControl>
                  <Textarea {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={submitting} className="gap-2">
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Salvar
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

// ─── Painel de detalhes (convite + participantes) ──────────────────────────────

function GroupDetailsPanel({ group }: { group: WhatsAppGroup }) {
  const [info, setInfo] = useState<WhatsAppGroupInfo | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [removeInput, setRemoveInput] = useState("");
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [[infoRes], [linkRes]] = await Promise.all([
      whatsappService.getGroup(group.groupId),
      whatsappService.getGroupInviteLink(group.groupId),
    ]);
    setInfo(infoRes);
    setInviteLink(linkRes?.invite_link || null);
    setLoading(false);
  }, [group.groupId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleResetLink() {
    setResetting(true);
    const [res, err] = await whatsappService.resetGroupInviteLink(group.groupId);
    setResetting(false);
    if (err || !res) {
      toast.error(err?.msg || "Erro ao gerar novo link");
      return;
    }
    setInviteLink(res.invite_link || null);
    toast.success("Novo link de convite gerado");
  }

  async function handleRemoveParticipants() {
    const participants = removeInput
      .split(/[,\n]/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (!participants.length) return;

    setRemoving(true);
    const [res, err] = await whatsappService.removeGroupParticipants(group.groupId, participants);
    setRemoving(false);

    if (err || !res?.success) {
      toast.error(err?.msg || "Erro ao remover participantes");
      return;
    }

    toast.success("Participante(s) removido(s)");
    setRemoveInput("");
    void load();
  }

  if (loading) {
    return (
      <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-md border border-border bg-muted/30 p-3">
      <div>
        <Label className="mb-1 block text-xs font-medium text-muted-foreground">
          Link de convite (grupos são invite-only)
        </Label>
        {inviteLink ? (
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-md border border-border bg-background px-3 py-2 text-xs font-mono">
              {inviteLink}
            </code>
            <CopyButton value={inviteLink} />
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Nenhum link disponível ainda.</p>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="mt-2 gap-1.5 text-xs h-7 px-2"
          disabled={resetting}
          onClick={() => void handleResetLink()}
        >
          {resetting ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Gerar novo link (invalida o atual)
        </Button>
      </div>

      <div>
        <Label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Users className="h-3.5 w-3.5" />
          Participantes ({info?.total_participant_count ?? info?.participants?.length ?? 0})
        </Label>
        {info?.participants?.length ? (
          <div className="flex flex-wrap gap-1.5">
            {info.participants.map((p) => (
              <span
                key={p.user}
                className="inline-flex items-center rounded-full border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground"
              >
                {p.user}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Ninguém entrou ainda — compartilhe o link de convite.
          </p>
        )}
      </div>

      <div>
        <Label className="mb-1 block text-xs font-medium text-muted-foreground">
          Remover participantes (telefone com DDI, separados por vírgula)
        </Label>
        <div className="flex items-center gap-2">
          <Input
            placeholder="5511999999999, 5511888888888"
            value={removeInput}
            onChange={(e) => setRemoveInput(e.target.value)}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="shrink-0 gap-1.5"
            disabled={removing || !removeInput.trim()}
            onClick={() => void handleRemoveParticipants()}
          >
            {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserMinus className="h-3.5 w-3.5" />}
            Remover
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Status da conta comercial oficial (OBA) ───────────────────────────────────

const OBA_STATUS_LABEL: Record<string, string> = {
  NOT_STARTED: "Nenhuma solicitação de OBA foi iniciada para este número",
  IN_REVIEW: "Solicitação de OBA em análise pela Meta",
  APPROVED: "Conta certificada como Official Business Account (OBA)",
  REJECTED: "Solicitação de OBA rejeitada pela Meta",
  SUSPENDED: "Status de OBA suspenso pela Meta",
};

/** A Meta não expõe endpoint para submeter a solicitação de OBA — só é possível pelo WhatsApp Manager. */
const WHATSAPP_MANAGER_URL = "https://business.facebook.com/latest/whatsapp_manager/";

function ObaStatusBanner() {
  const [status, setStatus] = useState<OBAStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const [res] = await whatsappService.getObaStatus();
      setStatus(res?.obaStatus ?? null);
      setLoading(false);
    }
    void load();
  }, []);

  if (loading) return <Skeleton className="h-9 w-full" />;

  const approved = status === "APPROVED";
  const canRequest = !approved && status !== "IN_REVIEW";
  const label = (status && OBA_STATUS_LABEL[status]) || "Não foi possível checar o status de OBA deste número";

  return (
    <div
      className={`flex flex-col gap-2 rounded-md border p-3 text-xs sm:flex-row sm:items-start sm:justify-between ${
        approved
          ? "border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950/30 dark:text-green-400"
          : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-400"
      }`}
    >
      <div className="flex items-start gap-2">
        {approved ? (
          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        ) : (
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        )}
        <p>
          {label}. A criação de grupos exige que a conta esteja certificada como Official Business
          Account (OBA). A Meta só permite solicitar isso pelo WhatsApp Manager — não existe API
          pública para submeter o pedido.
        </p>
      </div>
      {canRequest && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 shrink-0 gap-1.5 border-amber-300 bg-transparent px-2 text-xs text-amber-700 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-400 dark:hover:bg-amber-950/50"
          onClick={() => window.open(WHATSAPP_MANAGER_URL, "_blank", "noopener,noreferrer")}
        >
          <ExternalLink className="h-3 w-3" />
          Solicitar no WhatsApp Manager
        </Button>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Groups() {
  const [groups, setGroups] = useState<WhatsAppGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    const [data, err] = await whatsappService.listGroups();
    setLoading(false);
    if (err) {
      toast.error("Erro ao buscar grupos");
      return;
    }
    setGroups(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => {
    void fetchGroups();
  }, [fetchGroups]);

  async function handleDelete(g: WhatsAppGroup) {
    setDeletingId(g.groupId);
    const [res, err] = await whatsappService.deleteGroup(g.groupId);
    setDeletingId(null);

    if (err || !res?.success) {
      toast.error(err?.msg || "Erro ao remover grupo");
      return;
    }

    toast.success(`"${g.subject}" removido`);
    void fetchGroups();
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">Grupos de WhatsApp</h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setEditing(null);
              setShowForm((v) => !v);
            }}
            className="gap-2"
          >
            <PlusCircle className="h-4 w-4" />
            Novo grupo
          </Button>
        </div>
        <div className="mt-2">
          <ObaStatusBanner />
        </div>
      </div>

      {showForm && (
        <CreateGroupForm
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            void fetchGroups();
          }}
        />
      )}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Nenhum grupo criado ainda. Crie o primeiro grupo acima.
        </div>
      ) : (
        <div className="space-y-2">
          {groups.map((g) => {
            const isDeletingThis = deletingId === g.groupId;
            const isExpanded = expanded === g.groupId;
            const isEditing = editing === g.groupId;
            return (
              <div key={g.groupId} className="rounded-lg border border-border">
                <div className="flex items-center justify-between gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-sm">{g.subject}</p>
                    {g.description && (
                      <p className="truncate text-xs text-muted-foreground">{g.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 gap-1 px-2 text-xs"
                      onClick={() => setExpanded(isExpanded ? null : g.groupId)}
                    >
                      <Link2 className="h-3.5 w-3.5" />
                      {isExpanded ? "Ocultar" : "Convite e participantes"}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      aria-label="Editar"
                      onClick={() => {
                        setShowForm(false);
                        setEditing(isEditing ? null : g.groupId);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          aria-label="Excluir"
                          disabled={isDeletingThis}
                        >
                          {isDeletingThis ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir "{g.subject}"?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Isso remove o grupo no WhatsApp e no Baita Ticket. Essa ação não pode
                            ser desfeita.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => void handleDelete(g)}>
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>

                {isEditing && (
                  <div className="border-t border-border p-3">
                    <EditGroupForm
                      group={g}
                      onClose={() => setEditing(null)}
                      onSaved={() => {
                        setEditing(null);
                        void fetchGroups();
                      }}
                    />
                  </div>
                )}

                {isExpanded && (
                  <div className="border-t border-border p-3">
                    <GroupDetailsPanel group={g} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
